/**
 * REQUIRED, and not merely a formality: this module uses React context, and Next's App Router
 * refuses to build a server component that reaches one. Without this line the public site returns
 * 500 on every page — and nothing in typecheck, lint or the test suite says so, because none of
 * them render the site. It was found by loading the page.
 *
 * It is also correct on the merits. A form field is interactive by definition; there is no server
 * rendering of it to preserve.
 */
'use client';

import { clsx } from 'clsx';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { createContext, forwardRef, useContext, useId } from 'react';

/**
 * What the field rendered, told to the control inside it.
 *
 * `Input` used to GUESS: it pointed `aria-describedby` at `{id}-hint` whenever it had an id, while
 * `FormField` only renders that element when a `hint` prop is passed. Both sign-in fields shipped
 * pointing at elements that do not exist, which a screen reader resolves to nothing — so the
 * attribute was worse than absent, because it looked wired.
 *
 * The component that renders the description is the only one that knows whether it did, so it is
 * the one that says so. An `Input` outside a `FormField` gets `null` and describes nothing.
 */
const FieldContext = createContext<{ describedBy: string | undefined } | null>(null);

/**
 * A field, its label, and the two things that can be under it: helper text or an error.
 *
 * The error is wired up rather than merely rendered — `aria-describedby` points at it and
 * `aria-invalid` is set, so a screen reader reaching the field hears what is wrong with it. A red
 * border alone tells a sighted user something and everyone else nothing.
 *
 * Helper text and error occupy the same slot on purpose. Showing both means the useful sentence and
 * the corrective one compete, and the corrective one should win.
 */
export function FormField({
  label,
  hint,
  error,
  htmlFor,
  children,
  className,
}: {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor: string;
  children: ReactNode;
  className?: string;
}) {
  /* Whichever of the two slots actually rendered, or nothing. Never a guess. */
  const describedBy = error ? `${htmlFor}-error` : hint ? `${htmlFor}-hint` : undefined;

  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-[13px] font-semibold">
        {label}
      </label>

      <FieldContext.Provider value={{ describedBy }}>{children}</FieldContext.Provider>

      {error ? (
        <p id={`${htmlFor}-error`} role="alert" className="text-[12px] text-rust">
          {error}
        </p>
      ) : hint ? (
        /*
         * `ink-muted`, NOT `ink-subtle`, and the distinction is the whole point of the two tokens.
         * `textSubtle` is held to 3:1 because the spec designates it for eyebrows, column labels
         * and disabled states — hierarchy, not reading. Hint text is PROSE: it is the sentence that
         * tells somebody what to type, at 12px, and it has to clear 4.5:1 like any other sentence.
         *
         * Found by an accessibility scan of the registration form, and it is the same shape as the
         * focus-ring bug: a guard built for one category said nothing about a use just outside it.
         * The contrast suite held `textSubtle` to the bar it was designated for and had no opinion
         * about a component choosing it for something else.
         */
        <p id={`${htmlFor}-hint`} className="text-[12px] text-ink-2">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, ...props },
  ref,
) {
  const field = useContext(FieldContext);

  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      /* From the field that rendered the description, or from the caller. Never invented here. */
      aria-describedby={props['aria-describedby'] ?? field?.describedBy}
      className={clsx(
        'h-11 w-full rounded border bg-paper px-3.5 text-[15px]',
        'placeholder:text-ink-3',
        /*
         * THE BASE FOCUS RULE IS LEFT ALONE, and that is the fix.
         *
         * This used to set `focus:outline-none focus:ring-2 focus:ring-deep/25`, which threw away
         * the app's 2px navy `:focus-visible` outline — 12.55:1 — and replaced it with a ring
         * measuring **1.64:1**, under the 3:1 that WCAG 1.4.11 requires of a focus indicator. It
         * was also keyed to `focus:` rather than `focus-visible:`, so it fired on mouse click: the
         * halo the docstring above says it was written to avoid. The comment described a border
         * while the code drew a glow.
         *
         * The darker border on focus stays, as a second cue for the field you are actually in — but
         * on `focus-visible`, so a click does not light it, and never at the cost of the outline.
         */
        invalid ? 'border-rust' : 'border-control-border focus-visible:border-deep',
        className,
      )}
      {...props}
    />
  );
});

/**
 * A field whose control does not come from `Input` — a select, a radio group, a date picker.
 * Generates the id so the label and the error stay wired together without the caller inventing one.
 */
export function useFieldId(prefix: string): string {
  const generated = useId();
  return `${prefix}-${generated}`;
}
